import {useScopeQuery} from "@/core/store/scopeQuery";
import {AppClient, User, VorderCurrent} from "@/gql/gen";
import {useMenuStore} from "@/modules/menu/store/menu";
import {useRootStore} from "@/stores/root";
import {defineStore, storeToRefs} from "pinia";
import {computed, ref, watch} from "vue";
import normalizePhone from "@/core/util/format/normalizePhone";
import {randID} from "@/core/util/randID";
import {useGraphql} from "@/core/graphql/service";
import userLogoutMutation from "@/gql/gen/mutation/userLogoutMutation";
import {Cookies} from 'quasar'

const STORE_NAME = 'user'

export const useUserStore = defineStore("user", () => {

    const {registerScopeQuery, fetchScopes, fetchUserScopes} = useScopeQuery()
    const menuStore = useMenuStore()
    const rootStore = useRootStore()
    const {setRequestHeaders, responseSelection, useMutation} = useGraphql()

    const {storeGetter} = rootStore

    const {menuItems} = storeToRefs(menuStore)

    const user = ref<User | null>()

    const appClient = ref<AppClient | null>()
    const appClientId = ref<string | null>()
    const appClientToken = ref<string | null>()

    const userPhone = ref<string>('')

    const isAuthorized = computed(() => {
        return !!user.value
    })

    const menuPersonal = computed(() => {
        const items = menuItems.value.personal || []
        return items.map((item) => ({
            ...item,
            badgeLabel: item.badge ? (item.badge.getter ? storeGetter[item.badge.getter] : item.badge.label) : ''
        }))
    })

    const logoutRequest = useMutation(userLogoutMutation({
        ...responseSelection()
    }))

    function fillAppClient(client: AppClient) {
        appClient.value = client
        appClientId.value = client.CLIENT_ID
        appClientToken.value = client.TOKEN
    }

    function fillUser(_user: User) {
        user.value = _user
    }

    registerScopeQuery(STORE_NAME, 'user', {
        user_fetch: {
            __fragment: 'UserFields'
        },
        user_app_client: {
            ID: true,
            WEB_PUSH_ACTIVE: true,
            WEB_PUSH_ENDPOINT: true,
            WEB_PUSH_P256DH: true,
            WEB_PUSH_AUTH: true,
            CLIENT_ID: true,
            TOKEN: true,
            USER_ID: true,
        },
    }, (data) => {
        if (data.user_fetch)
            fillUser(data.user_fetch)

        if (data.user_app_client)
            fillAppClient(data.user_app_client)
    })

    watch(() => user.value?.PHONE, () => {
        if (user.value?.PHONE) {
            userPhone.value = normalizePhone(user.value.PHONE, 'raw')
        }
    }, {immediate: true})

    async function onAfterLogin(vorder?: VorderCurrent) {
        await fetchUserScopes(true)
    }

    function ensureClientId() {
        if (!appClientId.value) {
            appClientId.value = randID(10)
        }
        return appClientId.value
    }

    async function logout() {
        Cookies.remove('PHPSESSID')
        Cookies.remove('BITRIX_CONVERSION_CONTEXT_s1')
        Cookies.remove('HESTIASID')

        await logoutRequest.mutate({})
        window.location.replace('/')
    }

    setRequestHeaders('client', (headers) => {
        headers = {
            ...headers,
            'Client-Id': ensureClientId(),
            'Client-Token': appClientToken.value || '',
        }
        return headers as any
    })

    function haveSubscription(name: string) {
        const subscriptions = user.value?.SUBSCRIPTIONS || []
        return subscriptions.includes(name)
    }

    return {
        user,
        appClient,
        isAuthorized,
        menuPersonal,
        userPhone,
        onAfterLogin,
        ensureClientId,
        fillUser,
        logout,
        haveSubscription
    }
}, {
    persist: {
        pick: [
            'userPhone',
            'appClientId',
            'appClientToken',
        ],
    }
})

